import React, { useRef } from 'react';
import {
  ChevronLeftAtom,
  ChevronRightAtom,
} from '@atoms/icon-atoms/nav-icon-atoms';
import useWindowSize from '@hooks/use-window-size';
import vars from '@variables';
import 'glider-js/glider.min.css';
import {
  CarouselContainer,
  StyledArrowButton,
  StyledCarouselItem,
  StyledGlider,
} from './carousel.styles';
import { CarouselItemProps, CarouselProps } from './carousel.types';

const CarouselItem: React.FC<CarouselItemProps> = ({ children }) => {
  return <StyledCarouselItem>{children}</StyledCarouselItem>;
};

export function Carousel({
  items,
  visibleTilesCount,
  arrowTopSpacing,
  className,
}: CarouselProps) {
  const windowSize = useWindowSize();
  const leftArrowEl = useRef<HTMLButtonElement>(null);
  const rightArrowEl = useRef<HTMLButtonElement>(null);
  const isMobile = !!windowSize.width && windowSize.width < 768;
  const slidesToShow = isMobile ? 1.25 : visibleTilesCount;
  const slidesToScroll = isMobile ? 1 : visibleTilesCount;
  const hasArrows = slidesToShow < items.length;
  let calculatedArrowTopSpacing;

  if (!arrowTopSpacing) {
    switch (slidesToShow) {
      case 3:
        calculatedArrowTopSpacing = '24%';
        break;
      case 4:
        calculatedArrowTopSpacing = '16%';
        break;
      case 5:
        calculatedArrowTopSpacing = '12%';
        break;
      default:
        calculatedArrowTopSpacing = '30%';
    }
  }

  // Use provided spacing or fall back to calculated value
  const finalArrowTopSpacing = arrowTopSpacing || calculatedArrowTopSpacing;

  return (
    <CarouselContainer data-testid="carousel" className={className}>
      <StyledGlider
        className="glider-container"
        slidesToShow={slidesToShow}
        slidesToScroll={slidesToScroll}
        scrollLock={false}
        hasArrows={hasArrows}
        arrows={{
          prev: leftArrowEl.current,
          next: rightArrowEl.current,
        }}
        draggable={isMobile}
      >
        {items.map((item, index) => (
          <CarouselItem key={index}>{item}</CarouselItem>
        ))}
      </StyledGlider>
      {hasArrows && (
        <>
          <StyledArrowButton
            ref={leftArrowEl}
            aria-label="Previous"
            direction="left"
            data-testid="left-arrow"
            top={finalArrowTopSpacing}
          >
            <ChevronLeftAtom stroke={vars.emfBlue} />
          </StyledArrowButton>
          <StyledArrowButton
            ref={rightArrowEl}
            aria-label="Next"
            direction="right"
            data-testid="right-arrow"
            top={finalArrowTopSpacing}
          >
            <ChevronRightAtom stroke={vars.emfBlue} aria-label="Next" />
          </StyledArrowButton>
        </>
      )}
    </CarouselContainer>
  );
}
